import type { ComponentType } from 'react';

import type { SsoProvider } from '@/stores/brand-store';

/*
 * Provider marks.
 *
 * Drawn inline at the vendors' own colours: both Google and Microsoft require their
 * mark to be reproduced unmodified in sign-in buttons, so these are the one place on
 * this page that does not resolve through the palette.
 */

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.46a5.52 5.52 0 0 1-2.4 3.62v3.01h3.88c2.27-2.09 3.58-5.17 3.58-8.81z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24z"
      />
      <path fill="#FBBC05" d="M5.29 14.27a7.2 7.2 0 0 1 0-4.54v-3.1H1.28a12 12 0 0 0 0 10.74l4.01-3.1z" />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.63l4.01 3.1C6.23 6.86 8.88 4.75 12 4.75z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10.2v10.2H1z" />
      <path fill="#7FBA00" d="M12.8 1H23v10.2H12.8z" />
      <path fill="#00A4EF" d="M1 12.8h10.2V23H1z" />
      <path fill="#FFB900" d="M12.8 12.8H23V23H12.8z" />
    </svg>
  );
}

/** A neutral fallback, so a provider the frontend has never heard of still renders. */
function GenericMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden="true">
      <path
        d="M12 2.5 20.5 6v6c0 5-3.6 8.4-8.5 9.5C7.1 20.4 3.5 17 3.5 12V6z"
        className="fill-none stroke-primary"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const MARKS: Record<string, ComponentType> = {
  google: GoogleMark,
  microsoft: MicrosoftMark,
};

/**
 * The federated sign-in row.
 *
 * Renders nothing at all when the organisation has no providers configured — an
 * empty divider labelled "or continue with" above no buttons is worse than no
 * divider, and a disabled Google button reads as a broken integration rather than as
 * one that was never bought.
 *
 * Anchors rather than buttons: an authorisation handoff is a real navigation away
 * from the application, and an anchor gets the status bar preview, middle-click and
 * the browser's own back behaviour for free. `rel` is set because these leave the
 * origin.
 */
export function SsoProviders({ providers }: { providers: SsoProvider[] }) {
  if (providers.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
        <span className="text-[13px] font-medium text-muted-foreground">or continue with</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {providers.map((provider) => {
          const Mark = MARKS[provider.id] ?? GenericMark;

          return (
            <a
              key={provider.id}
              href={provider.start_url}
              rel="noopener"
              className={[
                'focus-ring inline-flex h-12 items-center justify-center gap-2.5 rounded-[0.875rem]',
                'border border-input bg-card px-4 text-[14px] font-medium text-foreground',
                'transition-[background-color,border-color,box-shadow] duration-hover ease-control',
                'hover:border-muted-subtle/70 hover:bg-background',
                'active:scale-[0.99]',
              ].join(' ')}
            >
              <Mark />
              {provider.name}
            </a>
          );
        })}
      </div>
    </div>
  );
}
